// Мінімальний fetch-клієнт для /admin/* endpoints. Не реюзує
// @helpstack/api-client бо там tenant-scope (Authorization з tenant-JWT).
// Sysadmin використовує власний JWT-issuer "helpstack-sysadmin".

// Fail-loud у production без VITE_API_URL; у dev — fallback на localhost:8080.
const BASE = import.meta.env.VITE_API_URL
  || (import.meta.env.PROD
    ? (() => { throw new Error('[superadmin] VITE_API_URL must be set in production build'); })()
    : 'http://localhost:8080');
const ACCESS_KEY = 'hsa_access';
const REFRESH_KEY = 'hsa_refresh';

export class AdminApi {
  private accessToken: string;
  private onLogout?: () => void;

  constructor() {
    this.accessToken = localStorage.getItem(ACCESS_KEY) || '';
  }

  setOnLogout(fn: () => void) {
    this.onLogout = fn;
  }

  setTokens(access: string, refresh: string) {
    this.accessToken = access;
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  }

  clearTokens() {
    this.accessToken = '';
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }

  hasToken(): boolean {
    return !!this.accessToken;
  }

  // Один проходом: при 401 пробуємо silent-refresh; якщо й він fail —
  // викликаємо onLogout для редіректу на login-сторінку.
  async fetch<T>(method: string, path: string, body?: unknown): Promise<T> {
    return this.doFetch<T>(method, path, body, true);
  }

  private async doFetch<T>(method: string, path: string, body: unknown, allowRetry: boolean): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;

    const res = await fetch(BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && allowRetry) {
      const ok = await this.tryRefresh();
      if (ok) return this.doFetch<T>(method, path, body, false);
      this.clearTokens();
      this.onLogout?.();
    }

    if (!res.ok) {
      let payload: { error?: string; code?: string; details?: Record<string, unknown> } = {};
      try {
        payload = await res.json();
      } catch {
        /* ignore */
      }
      const err = new Error(payload.error || `HTTP ${res.status}`) as Error & {
        status: number;
        code?: string;
        details?: Record<string, unknown>;
      };
      err.status = res.status;
      err.code = payload.code;
      err.details = payload.details;
      throw err;
    }

    if (res.status === 204) return null as unknown as T;
    return (await res.json()) as T;
  }

  private async tryRefresh(): Promise<boolean> {
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!refresh) return false;
    try {
      const res = await fetch(BASE + '/admin/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { access_token: string; refresh_token: string };
      this.setTokens(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    }
  }

  // Auth
  async login(email: string, password: string): Promise<TokensResp> {
    const out = await this.fetch<TokensResp>('POST', '/admin/auth/login', { email, password });
    this.setTokens(out.access_token, out.refresh_token);
    return out;
  }

  async logout(): Promise<void> {
    const refresh = localStorage.getItem(REFRESH_KEY) || '';
    try {
      await this.fetch('POST', '/admin/auth/logout', { refresh_token: refresh });
    } finally {
      this.clearTokens();
    }
  }

  me() {
    return this.fetch<AdminInfo>('GET', '/admin/me');
  }

  // Backups (superadmin-only)
  listBackups() {
    return this.fetch<BackupsResp>('GET', '/admin/backups');
  }

  createBackup() {
    return this.fetch<{ name: string }>('POST', '/admin/backups');
  }

  // Завантаження через fetch+blob: звичайний <a href> не передасть Bearer-токен.
  // Файл великий (десятки/сотні МБ) — тримається в пам'яті лише на час збереження.
  async downloadBackup(name: string): Promise<void> {
    const headers: Record<string, string> = {};
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    const res = await fetch(`${BASE}/admin/backups/${encodeURIComponent(name)}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Tenants
  listTenants(params: { status?: string; plan?: string; search?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.plan) qs.set('plan', params.plan);
    if (params.search) qs.set('search', params.search);
    qs.set('limit', String(params.limit ?? 50));
    qs.set('offset', String(params.offset ?? 0));
    return this.fetch<TenantsListResp>('GET', `/admin/tenants?${qs}`);
  }

  getTenant(id: string) {
    return this.fetch<TenantDetail>('GET', `/admin/tenants/${id}`);
  }

  suspendTenant(id: string, reason: string) {
    return this.fetch<void>('POST', `/admin/tenants/${id}/suspend`, { reason });
  }

  resumeTenant(id: string) {
    return this.fetch<void>('POST', `/admin/tenants/${id}/resume`);
  }

  archiveTenant(id: string) {
    return this.fetch<void>('POST', `/admin/tenants/${id}/archive`);
  }

  unarchiveTenant(id: string) {
    return this.fetch<void>('POST', `/admin/tenants/${id}/unarchive`);
  }

  hardDeleteTenant(id: string, confirmSlug: string) {
    return this.fetch<void>('DELETE', `/admin/tenants/${id}`, { confirm_slug: confirmSlug });
  }

  // Defaults
  getDefaults() {
    return this.fetch<DefaultsResp>('GET', '/admin/defaults');
  }
  updateDefaults(d: DefaultsResp) {
    return this.fetch<DefaultsResp>('PUT', '/admin/defaults', d);
  }
  /** Каталог пресетів (схем) авто-розподілу — для селектора у DefaultsView. */
  listRoutingPresets() {
    return this.fetch<{ items: RoutingPreset[] }>('GET', '/admin/routing/presets');
  }

  // Per-tenant plan + quota overrides
  getTenantPlan(id: string) {
    return this.fetch<TenantPlanResp>('GET', `/admin/tenants/${id}/plan`);
  }
  updateTenantPlan(id: string, input: TenantPlanInput) {
    return this.fetch<TenantPlanResp>('PUT', `/admin/tenants/${id}/plan`, input);
  }

  // 2026-05-29: Per-tenant add-ons понад базовий план (платні).
  getTenantAddons(id: string) {
    return this.fetch<TenantAddonsResp>('GET', `/admin/tenants/${id}/addons`);
  }
  updateTenantAddons(id: string, input: TenantAddonsInput) {
    return this.fetch<TenantAddonsResp>('PUT', `/admin/tenants/${id}/addons`, input);
  }

  // Metrics
  metrics() {
    return this.fetch<MetricsResp>('GET', '/admin/metrics');
  }

  // Файли — розмір вкладень і чистка старих.
  files() {
    return this.fetch<FilesResp>('GET', '/admin/files');
  }

  /** dry_run=true лише рахує; справжній запуск вимагає confirm = slug компанії. */
  purgeFiles(input: PurgeFilesInput) {
    return this.fetch<PurgeFilesResp>('POST', '/admin/files/purge', input);
  }

  // Diagnostics — стан БД/Redis/Centrifugo + realtime-кластер + хост.
  diagnostics() {
    return this.fetch<DiagnosticsResp>('GET', '/admin/diagnostics');
  }

  // Журнал realtime-подій (доставка: publish-fail, рестарти api).
  diagnosticsEvents() {
    return this.fetch<DiagEventsResp>('GET', '/admin/diagnostics/events');
  }

  // Billing info — Ф1 фінансового контуру
  getInvoiceSettings() {
    return this.fetch<InvoiceSettings>('GET', '/admin/invoice-settings');
  }
  updateInvoiceSettings(input: InvoiceSettings) {
    return this.fetch<InvoiceSettings>('PUT', '/admin/invoice-settings', input);
  }
  getTenantBillingInfo(tenantId: string) {
    return this.fetch<TenantBillingInfo>('GET', `/admin/tenants/${tenantId}/billing-info`);
  }
  updateTenantBillingInfo(tenantId: string, input: TenantBillingInfo) {
    return this.fetch<TenantBillingInfo>('PUT', `/admin/tenants/${tenantId}/billing-info`, input);
  }

  // Invoices — Ф2 фінансового контуру
  listInvoices(tenantId: string) {
    return this.fetch<InvoiceListResp>('GET', `/admin/tenants/${tenantId}/invoices`);
  }
  createInvoice(tenantId: string, input: CreateInvoiceInput) {
    return this.fetch<Invoice>('POST', `/admin/tenants/${tenantId}/invoices`, input);
  }
  sendInvoice(invoiceId: string) {
    return this.fetch<Invoice>('POST', `/admin/invoices/${invoiceId}/send`);
  }
  /**
   * Завантажує PDF-рахунок як файл (через blob).
   * Backend захищений RequireSysAdmin → не можна просто <a href>, бо браузер
   * не шле Authorization header на direct-navigation. Фетчимо з токеном і
   * тригеримо download через об'єкт-URL.
   */
  async downloadInvoicePDF(invoiceId: string, filename: string): Promise<void> {
    const headers: Record<string, string> = {};
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    const res = await fetch(BASE + `/admin/invoices/${invoiceId}/pdf`, { headers });
    if (!res.ok) {
      throw new Error(`Failed to download PDF: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ============ Ф3: receipts + extend subscription ============

  /**
   * Завантажити квитанцію (multipart) + продовжити підписку.
   * Рівно один з extend.months або extend.until має бути заданий.
   */
  async uploadInvoiceReceipt(
    invoiceId: string,
    file: File,
    extend: { months?: number; until?: string },
  ): Promise<UploadReceiptResp> {
    const fd = new FormData();
    fd.append('file', file);
    if (extend.months && extend.months > 0) fd.append('extend_months', String(extend.months));
    if (extend.until) fd.append('extend_until', extend.until);
    const headers: Record<string, string> = {};
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    const res = await fetch(BASE + `/admin/invoices/${invoiceId}/receipt`, {
      method: 'POST',
      headers,
      body: fd,
    });
    if (!res.ok) {
      let payload: { error?: string } = {};
      try { payload = await res.json(); } catch { /* ignore */ }
      throw new Error(payload.error || `HTTP ${res.status}`);
    }
    return (await res.json()) as UploadReceiptResp;
  }

  /** Скачати збережену квитанцію. */
  async downloadInvoiceReceipt(invoiceId: string, filename: string): Promise<void> {
    const headers: Record<string, string> = {};
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    const res = await fetch(BASE + `/admin/invoices/${invoiceId}/receipt`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// ============ Types ============
export interface AdminInfo {
  id: string;
  email: string;
  full_name: string;
  totp_enabled: boolean;
  last_login_at?: string;
  created_at: string;
}

export interface BackupItem {
  name: string;
  size_bytes: number;
  created_at: string;
}

export interface BackupsResp {
  items: BackupItem[];
  dir: string;
  retention_days: number;
  total_bytes: number;
  /** Чи доступний ручний запуск (налаштовано BACKUP_DIR + DSN). */
  can_create: boolean;
  /** Чи налаштовано off-site S3 — інакше показуємо попередження. */
  offsite_enabled: boolean;
}

export interface TokensResp {
  access_token: string;
  refresh_token: string;
  access_expires: string;
  refresh_expires: string;
  admin: AdminInfo;
}

export interface TenantListRow {
  id: string;
  slug: string;
  name: string;
  plan: 'start' | 'pro' | 'business' | string;
  status: 'active' | 'suspended' | 'archived' | 'deleted';
  archived_at?: string;
  suspended_at?: string;
  suspended_reason?: string;
  trial_ends_at?: string;
  created_at: string;
  updated_at: string;
  owner_email?: string;
  users_count: number;
  tickets_count: number;
  /** Дійсний до — найсвіжіший current_period_end з subscriptions; null = немає підписки. */
  subscription_expires_at: string | null;
}

export interface TenantsListResp {
  items: TenantListRow[];
  total: number;
}

export interface TenantDetail extends TenantListRow {
  archived_by?: string;
  suspended_by?: string;
  customers_count: number;
  /** Статус підписки: trialing|active|past_due|canceled тощо. */
  subscription_status?: string;
}

// ============ Billing info (Ф1 фінансового контуру) ============

/** Реквізити платформи (моя компанія, постачальник у рахунку). Singleton. */
export interface InvoiceSettings {
  company_name: string;
  edrpou: string;
  ipn: string;
  legal_address: string;
  bank_name: string;
  iban: string;
  mfo: string;
  phone: string;
  email: string;
  website: string;
  ceo_name: string;
  accountant_name: string;
  updated_at?: string;
  updated_by?: string;
}

/** Рахунок на оплату (Ф2 фінансового контуру). */
export interface Invoice {
  id: string;
  tenant_id: string;
  number: string;
  description: string;
  amount_cents: number;
  amount_uah: number;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'canceled' | string;
  period_start: string;
  period_end: string;
  due_date: string | null;
  sent_at: string | null;
  created_by?: string;
  created_at: string;
  /** Ф3: чи завантажена квитанція. */
  has_receipt: boolean;
  receipt_filename?: string;
  receipt_uploaded_at: string | null;
}

/** Ф3: відповідь POST /admin/invoices/{id}/receipt. */
export interface UploadReceiptResp {
  invoice: Invoice;
  /** Нова дата завершення підписки після продовження. */
  subscription_ends_at: string;
}

export interface InvoiceListResp {
  items: Invoice[];
}

/** Вхідні дані для створення рахунку. Number генерується автоматично на бекенді. */
export interface CreateInvoiceInput {
  description: string;
  amount_uah: number;
  period_start: string; // YYYY-MM-DD
  period_end: string;
  due_date?: string;
  subscription_id?: string;
}

/** Реквізити тенанта-покупця. Заповнює platform-адмін у TenantDrawer. */
export interface TenantBillingInfo {
  tenant_id: string;
  company_name: string;
  edrpou: string;
  ipn: string;
  legal_address: string;
  bank_name: string;
  iban: string;
  mfo: string;
  billing_email: string;
  billing_contact_name: string;
  updated_at?: string;
  updated_by?: string;
}

export interface TenantPlanResp {
  tenant_id: string;
  seats?: number | null;
  bots_total?: number | null;
  bots_active?: number | null;
  kb_public_spaces?: number | null;
  kb_internal_spaces?: number | null;
  amount_cents?: number | null;
  workspace_enabled?: boolean | null;
  note: string;
  updated_at?: string;
  updated_by?: string;
}

export interface TenantPlanInput {
  plan: 'start' | 'pro' | 'business' | 'enterprise';
  seats?: number | null;
  bots_total?: number | null;
  bots_active?: number | null;
  kb_public_spaces?: number | null;
  kb_internal_spaces?: number | null;
  amount_cents?: number | null;
  workspace_enabled?: boolean | null;
  note: string;
}

// 2026-05-29: Per-tenant add-ons понад план (платні).
export interface TenantAddonsResp {
  extra_operators: number;
  extra_kb_public: number;
  extra_kb_internal: number;
  extra_bots_active: number;
  extra_bots_total: number;
  monthly_price_uah: number;
}
export interface TenantAddonsInput {
  extra_operators: number;
  extra_kb_public: number;
  extra_kb_internal: number;
  extra_bots_active: number;
  extra_bots_total: number;
}

export interface DefaultsResp {
  default_plan: string;
  default_language: string;
  default_timezone: string;
  trial_days: number;
  welcome_email_subject: string;
  welcome_email_body: string;
  onboarding_steps: unknown;
  // Phase T5: routing-defaults для нових тенантів.
  default_routing_algorithm: 'least_busy' | 'round_robin';
  default_ar_in_hours: string;
  default_ar_after_hours: string;
  // Phase Presets (2026-05-24): ключ пресету авто-розподілу, що сіється
  // новому тенанту при signup. Значення з GET /admin/routing/presets.
  default_preset_key: string;
  updated_at?: string;
  updated_by?: string;
}

/** Пресет авто-розподілу — для селектора у DefaultsView. */
export interface RoutingPreset {
  key: string;
  name: string;
  description: string;
  summary: string[];
  icon: string;
}

export interface FilesResp {
  files: number;
  bytes: number;
  older_3m: { files: number; bytes: number };
  older_6m: { files: number; bytes: number };
  older_12m: { files: number; bytes: number };
  purged_files: number;
  purged_bytes: number;
  tenants: {
    tenant_id: string;
    tenant_slug: string;
    tenant_name: string;
    files: number;
    bytes: number;
    oldest: string;
  }[];
}

export interface PurgeFilesInput {
  tenant_id: string;
  /** РРРР-ММ-ДД: підуть файли, СТАРІШІ за цю дату. */
  before: string;
  dry_run: boolean;
  /** Slug компанії — підтвердження; для dry_run не потрібен. */
  confirm?: string;
}

export interface PurgeFilesResp {
  files: number;
  bytes: number;
  remaining_files: number;
  remaining_bytes: number;
  dry_run: boolean;
  failed: number;
}

export interface MetricsResp {
  tenants: {
    active: number;
    suspended: number;
    archived: number;
    start: number;
    pro: number;
    business: number;
    enterprise: number;
    in_trial: number;
  };
  users: { active_total: number };
  tickets: { total: number; last_30d: number };
  customers: { total: number };
  database: { size_bytes: number; size_mb: number };
  server: {
    available: boolean;
    disk: {
      total_bytes: number;
      free_bytes: number;
      used_bytes: number;
      used_pct: number;
      mount_point: string;
    };
    memory: {
      total_bytes: number;
      free_bytes: number;
      used_bytes: number;
      used_pct: number;
    };
    cpu: {
      cores: number;
      load1: number;
      load5: number;
      load15: number;
      load1_pct: number;
    };
  };
}

export interface DiagnosticsResp {
  checked_at: string;
  status: 'ok' | 'degraded';
  components: { name: string; status: 'ok' | 'down' | 'skipped'; detail?: string }[];
  realtime: {
    available: boolean;
    num_nodes: number;
    num_clients: number;
    num_users: number;
    num_channels: number;
  };
  /** Той самий shape, що MetricsResp.server (disk/memory/cpu). */
  server: MetricsResp['server'];
  duration_ms: number;
}

export interface DiagEvent {
  at: string;
  kind: 'publish_fail' | 'api_start' | string;
  detail: string;
  channel?: string;
}

export interface DiagEventsResp {
  events: DiagEvent[];
}

export const api = new AdminApi();
